import { GlobalSettingsSearch } from '../search/SettingsSearch';
import { SearchFocus } from '../search/SearchFocus';
import '../search/search.css';
import { updatesApi } from '../api/updates';
import { UpdateBanner } from './UpdateBanner';
import { BrandMark } from './BrandMark';
import { Badge } from './ui';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { MobileNavigation } from './MobileNavigation';
import { PageTransition } from './motion';
import { api } from '../api/client';
import { useApp } from '../app-context';
import { roleLabel } from '../format';
import { usePoll } from '../hooks/usePoll';
import { runningClientPackTests } from './ClientPackChecks';

const groups = [
  { label: '運用', paths: ['/', '/requests', '/jobs'] },
  { label: '構成', paths: ['/templates', '/routing'] },
  { label: '管理', paths: ['/audit', '/users', '/settings'] },
];
const links = [
  { to: '/', label: 'ダッシュボード', end: true },
  { to: '/requests', label: '承認' },
  { to: '/templates', label: 'テンプレート' },
  { to: '/routing', label: '接続先' },
  { to: '/jobs', label: 'ジョブ' },
  { to: '/audit', label: '監査ログ' },
  { to: '/users', label: '利用者', ownerOnly: true },
  { to: '/settings', label: '設定' },
];

export function Layout() {
  const { me, refreshMe } = useApp();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const title =
    pathname === '/servers/new'
      ? 'サーバーを作成'
      : pathname.startsWith('/servers/')
        ? 'サーバー詳細'
        : (links.find((link) => link.to === pathname)?.label ?? 'mc-panel');
  const user = me?.user;
  const version = usePoll(updatesApi.version, 60_000);
  const pending = usePoll(() => api.requests({ status: 'pending' }), 10000);
  const pendingCount = pending.data?.length ?? 0;
  // クライアント起動テストは VM を作ってメモリを使うので、どの画面からも動いていると分かるようにする
  const tests = usePoll(() => api.clientPackChecks(), 10000);
  const testCount = runningClientPackTests(tests.data);

  const logout = async () => {
    await api.logout();
    await refreshMe();
    navigate('/login');
  };

  const nav = groups.map((group) => (
    <div className="nav-group" key={group.label}>
      <h2 className="nav-heading">{group.label}</h2>
      {links
        .filter((link) => group.paths.includes(link.to))
        .filter((link) => !link.ownerOnly || user?.role === 'owner')
        .map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.end}
            className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
          >
            {link.label}
            {link.to === '/requests' && pendingCount > 0 && <Badge tone="warn">承認待ち {pendingCount} 件</Badge>}
            {link.to === '/jobs' && testCount > 0 && <Badge tone="info">テスト実行中 {testCount} 件</Badge>}
          </NavLink>
        ))}
    </div>
  ));

  const menu = (
    <>
      <div className="brand">
        <BrandMark />
        mc-panel
      </div>
      <nav aria-label="メイン">{nav}</nav>
      <div className="sidebar-footer">
        <span className="small faint">mc-panel {version.data?.version ?? '…'}</span>
        {user && (
          <div className="user-chip">
            <span>
              {user.name} <span className="faint small">{roleLabel[user.role]}</span>
            </span>
          </div>
        )}
        <button className="btn ghost small" onClick={() => void logout()}>
          ログアウト
        </button>
      </div>
    </>
  );
  return (
    <div className="shell">
      <aside className="sidebar desktop-sidebar">{menu}</aside>
      <div style={{ minWidth: 0, position: 'relative' }}>
        <MobileNavigation title={title}>{menu}</MobileNavigation>
        <div className="global-search-bar">
          <GlobalSettingsSearch />
        </div>
        <SearchFocus />
        <main className="main">
          <UpdateBanner data={version.data} role={user?.role} />
          <PageTransition key={pathname}>
            <Outlet />
          </PageTransition>
        </main>
      </div>
    </div>
  );
}
